import React, { useState, useEffect, useMemo } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { toast } from 'react-toastify';
import { getProfile, updateProfile, getUsers } from '../../../models/userModel';
import Button from '../../../components/common/Button';
import SearchableSelect from '../../../components/common/SearchableSelect';
import TextInput from '../../../components/common/TextInput';
import { useAuth } from '../../../context/AuthContext';

const schema = yup.object().shape({
  full_name: yup.string().required('Full Name is required'),
  email: yup.string().email('Invalid email format').required('Email is required'),
  phone: yup.string().nullable(),
  password: yup.string().nullable(),
  user_delegate: yup.number().nullable(),
  user_otp: yup.number().nullable(),
});

function Profile() {
  const { user: authUser, updateUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [users, setUsers] = useState([]);
  const { control, handleSubmit, reset, formState: { errors } } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      full_name: '',
      email: '',
      phone: '',
      password: '',
      user_delegate: null,
      user_otp: null,
    }
  });

  useEffect(() => {
    async function fetchData() {
      try {
        // Try to fetch profile data first
        const profileResponse = await getProfile();
        
        if (profileResponse.status === 1) {
          const profileData = profileResponse.data;
          reset({
            full_name: profileData.user_full_name,
            email: profileData.user_email,
            phone: profileData.user_phone,
            user_delegate: profileData.user_delegate,
            user_otp: profileData.user_otp,
            password: '', // Ensure password is always a controlled field
          });
          setUser(profileData);
        }

        // Try to fetch users for delegate dropdown, but don't fail if it doesn't work
        try {
          // Fetch all users for delegate dropdown (no pagination) using 'all=true' parameter
          const params = new URLSearchParams({
            all: 'true'
          });
          const usersResponse = await getUsers(params);
          if (usersResponse.status === 1) {
            // The 'all=true' parameter returns users directly, not in a paginated structure
            setUsers(Array.isArray(usersResponse.data) ? usersResponse.data : []);
          }
        } catch (usersError) {
          console.warn('Could not fetch users for delegate dropdown:', usersError);
          // Continue without users data - delegate dropdown will be empty
        }
      } catch (error) {
        toast.error('Failed to load profile data.');
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [reset]);

  const delegateOptions = useMemo(() => {
    if (!users || !Array.isArray(users) || !user) return [];
    const filteredUsers = users.filter(u => u.user_id !== user.user_id);
    return filteredUsers.map(u => ({ value: u.user_id, label: u.user_full_name }));
  }, [users, user]);

  const onSubmit = async (data) => {
    try {
      const response = await updateProfile(data);
      if (response.status === 1) {
        toast.success('Profile updated successfully!');
        if (authUser.user_id === response.data.user_id) {
          updateUser(response.data);
        }
      } else {
        toast.error(response.message || 'Failed to update profile.');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'An error occurred.');
    }
  };

  if (loading) {
    return <div>Loading profile...</div>;
  }

  return (
    <div className="p-4 py-6 md:p-8 space-y-6 md:space-y-8">
      <h1 className="text-2xl lg:text-3xl font-bold custom-text-neutral-800">My Profile</h1>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
          {/* Left Column */}
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white p-6 rounded-xl shadow-lg">
              <h2 className="text-xl font-semibold custom-text-neutral-800 mb-4">Account Details</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium custom-text-neutral-700 mb-1">Full Name</label>
                  <Controller name="full_name" control={control} render={({ field }) => <TextInput {...field} />} />
                  {errors.full_name && <p className="custom-text-accent-danger text-xs mt-1">{errors.full_name.message}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium custom-text-neutral-700 mb-1">Email Address</label>
                  <Controller name="email" control={control} render={({ field }) => <TextInput {...field} type="email" />} />
                  {errors.email && <p className="custom-text-accent-danger text-xs mt-1">{errors.email.message}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium custom-text-neutral-700 mb-1">Cellphone Number</label>
                  <Controller name="phone" control={control} render={({ field }) => <TextInput {...field} />} />
                  {errors.phone && <p className="custom-text-accent-danger text-xs mt-1">{errors.phone.message}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium custom-text-neutral-700 mb-1">Login OTP</label>
                  <Controller
                    name="user_otp"
                    control={control}
                    render={({ field }) => (
                      <SearchableSelect
                        options={[
                          { value: 1, label: 'SMS' },
                          { value: 2, label: 'Email' },
                          { value: 3, label: 'Both' }
                        ]}
                        value={[
                          { value: 1, label: 'SMS' },
                          { value: 2, label: 'Email' },
                          { value: 3, label: 'Both' }
                        ].find(opt => opt.value === field.value) || null}
                        onChange={option => field.onChange(option ? option.value : null)}
                        placeholder="Select OTP method..."
                      />
                    )}
                  />
                  {errors.user_otp && <p className="custom-text-accent-danger text-xs mt-1">{errors.user_otp.message}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium custom-text-neutral-700 mb-1">New Password</label>
                  <Controller name="password" control={control} render={({ field }) => <TextInput {...field} type="password" placeholder="Leave blank to keep current password" />} />
                  {errors.password && <p className="custom-text-accent-danger text-xs mt-1">{errors.password.message}</p>}
                </div>
              </div>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-lg">
              <h2 className="text-xl font-semibold custom-text-neutral-800 mb-4">Roles & Delegation</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <label className="block text-sm font-medium custom-text-neutral-700 mb-1">Role</label>
                  <div className="w-full px-4 py-2.5 bg-neutral-100 custom-text-neutral-700 rounded-lg shadow-sm border custom-border-neutral-200">
                    {user?.role_name || 'N/A'}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium custom-text-neutral-700 mb-1">Division</label>
                  <div className="w-full px-4 py-2.5 bg-neutral-100 custom-text-neutral-700 rounded-lg shadow-sm border custom-border-neutral-200">
                    {user?.department_name || 'N/A'}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium custom-text-neutral-700 mb-1">Delegate</label>
                  <Controller
                    name="user_delegate"
                    control={control}
                    render={({ field }) => (
                      <SearchableSelect
                        options={delegateOptions}
                        value={field.value || ''}
                        onChange={(value) => field.onChange(value === '' || value === null ? 0 : value)}
                        placeholder="Select a delegate..."
                        allText="None"
                        showSearch={true}
                        className=""
                      />
                    )}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Right Column */}
          <div className="lg:col-span-1 space-y-6">
            <div className="bg-white p-6 rounded-xl shadow-lg">
              <h2 className="text-xl font-semibold custom-text-neutral-800 mb-4">Actions</h2>
              <div className="flex flex-col gap-3">
                <Button type="submit" variant="primary" className="w-full justify-center">Update Profile</Button>
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}

export default Profile;
